"use client";

import { useState, useEffect, useRef } from "react";
import { X, Send, ChevronDown, ChevronUp, MessageSquare, Loader2, Image as ImageIcon } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Avatar, AvatarFallback } from "@/app/_module/components/ui/avatar";
import { cn } from "@/app/_module/lib/utils";
import { useUnifiedDialer } from "@/app/_module/contexts/UnifiedDialerContext";
import { getSmsFailureReason } from "@/app/_module/utils/smsFailureReason";

interface Message {
  id: string;
  text: string;
  direction: "inbound" | "outbound";
  timestamp: Date;
  // Full carrier status spectrum so the widget reflects the actual lifecycle:
  // sending → sent → delivered (or queued/failed/undelivered).
  status?: "sending" | "queued" | "sent" | "delivered" | "failed" | "undelivered";
  mediaUrls?: string[]; // MMS media attachments
  errorCode?: string | null; // carrier error code on a failed message
  errorMessage?: string | null; // raw error description on a failed message
}

// Map carrier statuses to user-friendly labels + tint.
const statusDisplay = (status?: Message["status"]) => {
  switch (status) {
    case "sending":
    case "queued":
      return { label: "Sending…", className: "" };
    case "sent":
      return { label: "Sent", className: "" };
    case "delivered":
      return { label: "Delivered ✓✓", className: "" };
    case "failed":
    case "undelivered":
      return { label: "Failed — tap to retry", className: "text-red-100" };
    default:
      return null;
  }
};

// Outbound bubble color reflects the lifecycle so the user can tell at a
// glance: gray = in-flight, blue = handed off to Twilio, green = delivered,
// red = failed. Inbound is always neutral white.
const outboundBubbleClass = (status?: Message["status"]): string => {
  switch (status) {
    case "sending":
    case "queued":
      return "bg-slate-400 text-white";
    case "delivered":
      return "bg-emerald-600 text-white";
    case "failed":
    case "undelivered":
      return "bg-red-600 text-white";
    case "sent":
    default:
      return "bg-blue-600 text-white";
  }
};

interface SmsChatWidgetProps {
  leadName: string;
  phoneNumber: string;
  leadId: string;
  messages?: Message[];
  onSendMessage: (message: string) => Promise<void>;
  onClose: () => void;
  onMinimize?: () => void;
}

export const SmsChatWidget = function ({
  leadName,
  phoneNumber,
  leadId,
  messages = [],
  onSendMessage,
  onClose,
  onMinimize,
}: SmsChatWidgetProps) {
  const { isMinimized: isDialerMinimized } = useUnifiedDialer();
  const [isMinimized, setIsMinimized] = useState(false);

  // Draft persistence: same pattern as LeadNotes — localStorage keyed by
  // leadId, restored on mount, debounced auto-save, cleared on successful
  // send. Survives page refresh and tab swap.
  const DRAFT_KEY = leadId ? `sms_draft_${leadId}` : null;
  const [messageText, setMessageText] = useState(() => {
    if (typeof window !== 'undefined' && DRAFT_KEY) {
      try {
        const saved = localStorage.getItem(DRAFT_KEY);
        if (saved) return saved;
      } catch {}
    }
    return "";
  });
  const [isSending, setIsSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const draftTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Auto-save draft to localStorage 2s after last keystroke. Clear when empty.
  useEffect(() => {
    if (!DRAFT_KEY) return;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);

    draftTimerRef.current = setTimeout(() => {
      try {
        if (messageText.trim()) {
          localStorage.setItem(DRAFT_KEY, messageText);
        } else {
          localStorage.removeItem(DRAFT_KEY);
        }
      } catch {}
    }, 2000);

    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [messageText, DRAFT_KEY]);

  // Force-save the latest draft on unmount (covers route changes / widget close
  // before the 2s debounce fires).
  const messageTextRef = useRef(messageText);
  messageTextRef.current = messageText;
  useEffect(() => {
    if (!DRAFT_KEY) return;
    return () => {
      try {
        const current = messageTextRef.current;
        if (current.trim()) {
          localStorage.setItem(DRAFT_KEY, current);
        }
      } catch {}
    };
  }, [DRAFT_KEY]);

  const handleSend = async () => {
    if (!messageText.trim() || isSending) return;

    setIsSending(true);
    try {
      await onSendMessage(messageText.trim());
      setMessageText("");
      // Send succeeded — drop the draft. On failure we keep it so the user
      // can retry without retyping.
      if (DRAFT_KEY) {
        try { localStorage.removeItem(DRAFT_KEY); } catch {}
      }
    } catch (error) {

    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (isMinimized) {
    return (
      <div className={`fixed bottom-4 z-[60] ${!isDialerMinimized ? 'right-[420px]' : 'right-4'}`}>
        <Button
          onClick={() => setIsMinimized(false)}
          className="rounded-full h-14 w-14 bg-blue-600 hover:bg-blue-700 shadow-lg"
        >
          <MessageSquare className="h-6 w-6" />
        </Button>
      </div>
    );
  }

  return (
    <div className={`fixed bottom-4 z-[60] w-96 bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col max-h-[600px] ${!isDialerMinimized ? 'right-[420px]' : 'right-4'}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Avatar className="h-10 w-10 border-2 border-white">
            <AvatarFallback className="bg-blue-500 text-white">
              {getInitials(leadName)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm truncate">{leadName}</h3>
            <p className="text-xs text-blue-100 truncate">{phoneNumber}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setIsMinimized(true);
              onMinimize?.();
            }}
            className="h-8 w-8 p-0 hover:bg-blue-500"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 hover:bg-blue-500"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 p-4 bg-gray-50 overflow-y-auto" ref={scrollRef}>
        <div className="space-y-3">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <MessageSquare className="h-12 w-12 text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">No messages yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Send a message to start the conversation
              </p>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "flex",
                  message.direction === "outbound" ? "justify-end" : "justify-start"
                )}
              >
                <div
                  className={cn(
                    "max-w-[75%] rounded-lg px-4 py-2 shadow-sm transition-colors",
                    message.direction === "outbound"
                      ? outboundBubbleClass(message.status)
                      : "bg-white text-gray-900 border border-gray-200"
                  )}
                >
                  {/* Render MMS media attachments */}
                  {message.mediaUrls && message.mediaUrls.length > 0 && (
                    <div className="mb-2 space-y-2">
                      {message.mediaUrls.map((url, index) => (
                        <a
                          key={index}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={url}
                            alt={`MMS attachment ${index + 1}`}
                            className="max-w-full rounded-md max-h-48 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                            onError={(e) => {
                              // If image fails to load, show a placeholder
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              target.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                          <div className={cn(
                            "hidden items-center gap-2 p-3 rounded-md",
                            message.direction === "outbound" ? "bg-blue-500" : "bg-gray-100"
                          )}>
                            <ImageIcon className="h-5 w-5" />
                            <span className="text-sm">View Attachment</span>
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                  {/* Render message text (skip if it's just the placeholder) */}
                  {message.text && message.text !== '[MMS Attachment]' && (
                    <p className="text-sm whitespace-pre-wrap break-words">
                      {message.text}
                    </p>
                  )}
                  {/* Show placeholder if MMS-only with no visible text */}
                  {(!message.text || message.text === '[MMS Attachment]') && message.mediaUrls && message.mediaUrls.length > 0 && (
                    <p className={cn(
                      "text-xs italic",
                      message.direction === "outbound" ? "text-blue-200" : "text-gray-400"
                    )}>
                      {message.mediaUrls.length} attachment{message.mediaUrls.length !== 1 ? 's' : ''}
                    </p>
                  )}
                  <div
                    className={cn(
                      "flex items-center gap-2 mt-1 text-xs",
                      message.direction === "outbound"
                        ? "text-blue-100 justify-end"
                        : "text-gray-500"
                    )}
                  >
                    <span>{formatTime(message.timestamp)}</span>
                    {message.direction === "outbound" && (() => {
                      const sd = statusDisplay(message.status);
                      return sd ? <span className={cn(sd.className)}>• {sd.label}</span> : null;
                    })()}
                  </div>
                  {message.direction === "outbound" &&
                    (message.status === "failed" || message.status === "undelivered") &&
                    (() => {
                      const reason = getSmsFailureReason(message.errorCode, message.errorMessage);
                      return reason ? (
                        <p className="text-xs text-red-100 mt-0.5 text-right" title={reason.hint}>
                          {reason.label}
                        </p>
                      ) : null;
                    })()}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Input Area */}
      <div className="p-4 bg-white border-t border-gray-200 rounded-b-lg">
        <div className="flex gap-2">
          <Input
            placeholder="Type a message..."
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isSending}
            className="flex-1"
          />
          <Button
            onClick={handleSend}
            disabled={!messageText.trim() || isSending}
            className="bg-blue-600 hover:bg-blue-700"
            size="icon"
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};
